import type { SupabaseClient } from '@supabase/supabase-js';
import { RAW_KEY, type SystemKey } from './status-timestamps';

/**
 * Shared, system_key-driven job-status classification (Logic #5).
 *
 * `job_statuses` is account-customisable — a workspace can rename
 * "in_progress" to "On the road". Every place that compared the raw status
 * NAME ("status === 'in_progress'") silently broke for renamed accounts, so
 * the same count → list navigation diverged across the app (the audit found
 * 4 different definitions of "active jobs for client").
 *
 * These helpers resolve a status to its stable `system_key` first, so they
 * give IDENTICAL results to the old string checks on default-status accounts
 * (via the RAW_KEY fallback) and CORRECT results on renamed ones.
 *
 * Two map shapes are accepted because the callers already hold different
 * ones: the page components build a plain `Record<name, system_key>`; loaders
 * have the raw `job_statuses` rows.
 */
export type StatusKeyMap = Record<string, SystemKey | string | null>;

/** Build the name → system_key lookup from job_statuses rows. */
export function buildStatusKeyMap(
  rows: { name: string; system_key: string | null }[],
): StatusKeyMap {
  const map: StatusKeyMap = {};
  for (const r of rows) map[r.name] = (r.system_key as SystemKey) ?? null;
  return map;
}

/**
 * Resolve a status name to its system_key. Prefers the account's own
 * job_statuses mapping; falls back to the known default/seeded names so a
 * caller without the account map (or a status not in it) still classifies
 * the system defaults correctly.
 */
export function resolveStatusKey(
  status: string | null | undefined,
  keyMap?: StatusKeyMap,
): SystemKey {
  if (!status) return null;
  if (keyMap && status in keyMap) return (keyMap[status] as SystemKey) ?? null;
  return RAW_KEY[status] ?? null;
}

/**
 * Raw status NAMES meaning "booked in, not started yet" — i.e. every default
 * status whose system_key is 'scheduled'.
 *
 * For the handful of queries that filter `jobs.status` by literal name rather
 * than going through the account's job_statuses map (crons, delete guards, the
 * On Time Tracker). Those were all written as `.eq('status', 'scheduled')` when
 * 'scheduled' was the only such name; adding 'travelling' silently narrowed
 * them, so an engineer marking themselves en route would have dropped out of
 * the tracker, out of the client's active-job count and out of the owner's
 * "nobody has started this job" alert.
 *
 * Prefer `statusNamesForKeys` with the account's real rows where you have them
 * — this constant still can't see a status the account renamed.
 */
export const NOT_STARTED_STATUS_NAMES = ['scheduled', 'travelling'] as const;

/**
 * The seeded, `is_system` status name meaning "set off, not there yet".
 *
 * Deliberately matched by NAME rather than by system_key: 'travelling' SHARES
 * `system_key: 'scheduled'` with Scheduled itself (see migration
 * 20260803095916 — 'active' would stamp actual_start and corrupt every On Time
 * Tracker figure), so the key cannot tell the two apart. This is what the
 * "On my way" button writes and what the on-my-way text fires on.
 */
export const TRAVELLING_STATUS_NAME = 'travelling';

/** "Active" = live/ongoing work: scheduled OR in-progress (system_key). */
export function isActiveKey(key: SystemKey): boolean {
  return key === 'scheduled' || key === 'active';
}

/** A job counts as active if its status resolves to a scheduled/active key. */
export function isJobActive(
  status: string | null | undefined,
  keyMap?: StatusKeyMap,
): boolean {
  return isActiveKey(resolveStatusKey(status, keyMap));
}

/** Convenience predicates for the other system_keys. */
export function isJobInProgress(status: string | null | undefined, keyMap?: StatusKeyMap): boolean {
  return resolveStatusKey(status, keyMap) === 'active';
}
export function isJobScheduled(status: string | null | undefined, keyMap?: StatusKeyMap): boolean {
  return resolveStatusKey(status, keyMap) === 'scheduled';
}
export function isJobCompleted(status: string | null | undefined, keyMap?: StatusKeyMap): boolean {
  return resolveStatusKey(status, keyMap) === 'completed';
}
export function isJobCancelled(status: string | null | undefined, keyMap?: StatusKeyMap): boolean {
  return resolveStatusKey(status, keyMap) === 'cancelled';
}

/**
 * "Terminal" = the job is finished or dead, so it no longer OCCUPIES its diary
 * slot even though `scheduled_start` still points at it.
 *
 * Completing a job never moves `scheduled_start` — that's deliberate, because
 * the on-time tracker's whole premise is promised-time vs actual-time, and
 * rewriting the promise on completion would erase the record of turning up
 * early. The consequence is that a job done a day early stays sat in
 * tomorrow's date window, so every FORWARD-looking query has to ask "is this
 * still outstanding?" rather than just "is it in range?".
 *
 * Skipping that check is what made Mucka tell an owner he was "booked in
 * tomorrow" on a job he'd already finished and invoiced.
 *
 * Deliberately NOT for backward-looking reads: today's job list should keep
 * completed jobs, or the end-of-day briefing can't say how many got done.
 *
 * 🚨 And it is NOT the predicate for "should this appear on the diary". Since
 * 2026-09-10 the diary hides CANCELLED work and keeps COMPLETED work — a job
 * finished early still sits in a future slot and the owner needs to see it got
 * done. Terminal conflates the two. Use `withoutCancelledJobs` from
 * `./diary-visibility` for anything that draws work against a clock.
 */
export function isJobTerminal(
  status: string | null | undefined,
  keyMap?: StatusKeyMap,
): boolean {
  const key = resolveStatusKey(status, keyMap);
  return key === 'completed' || key === 'cancelled';
}

/**
 * Load the account's status name → system_key map.
 *
 * Prefer `buildStatusKeyMap` when the caller already has `job_statuses` rows in
 * hand. This exists for the callers that don't (and would otherwise fall back
 * to raw-name comparisons, which silently break on a renamed status): it's one
 * small indexed lookup, cheap enough to Promise.all alongside the query it's
 * classifying.
 *
 * Returns `{}` on error rather than throwing — a failed status lookup should
 * degrade to the RAW_KEY defaults, not take the caller's feature offline.
 */
export async function fetchStatusKeyMap(
  supabase: SupabaseClient,
  accountId: string,
): Promise<StatusKeyMap> {
  const { data, error } = await supabase
    .from('job_statuses')
    .select('name, system_key')
    .eq('account_id', accountId);
  if (error || !Array.isArray(data)) return {};
  return buildStatusKeyMap(data as { name: string; system_key: string | null }[]);
}

/**
 * Words a person uses for a status, mapped to the system_key they mean.
 *
 * WHY THIS EXISTS. `query_records` applied `filters.status` as a raw equality,
 * so an owner asking "which jobs did Owen COMPLETE on the 15th" produced
 * `.eq('status', 'completed')` — and on an account whose completed work all
 * sits at `to_invoice`, that matches NOTHING. Zero rows, on a day with five
 * jobs on it. The pilot account this was found on (2026-08-20) has FOUR names
 * on `system_key: 'completed'` — `to_invoice` (31,173 rows), `invoice_sent`,
 * `payment_issues` and `completed` (0 rows) — so the one name the model
 * reaches for first is the only one that can never match.
 *
 * That is CLAUDE.md anti-pattern #14 on the read path: `'completed'` is not a
 * status test, because `job_statuses` is renameable per account and several
 * names share a key.
 *
 * "Attended" earns its place here for the same reason. A trades owner says his
 * engineer ATTENDED a job, and asking him to say "completed" instead — as the
 * pilot offered to do — is making the customer work around us.
 */
const STATUS_TERM_KEYS: Record<string, Exclude<SystemKey, null>> = {
  completed: 'completed',
  complete: 'completed',
  completion: 'completed',
  done: 'completed',
  finished: 'completed',
  finish: 'completed',
  // The owner's own vocabulary — "been to", "went to", "attended site".
  attended: 'completed',
  attend: 'completed',
  been: 'completed',
  went: 'completed',
  visited: 'completed',
  cancelled: 'cancelled',
  canceled: 'cancelled',
  scheduled: 'scheduled',
  booked: 'scheduled',
  upcoming: 'scheduled',
  in_progress: 'active',
  active: 'active',
  started: 'active',
  ongoing: 'active',
  pending: 'pending',
  new: 'pending',
};

/** Lowercase, and treat spaces and hyphens as underscores ("in progress"). */
function normaliseStatusTerm(term: string): string {
  return term.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

/**
 * Turn a status term the model supplied into the account status NAMES to
 * filter on.
 *
 * 🚨 Order matters, and the obvious order is wrong. A MEANING word is checked
 * BEFORE an exact name match, even though 'completed' is usually also a status
 * name. Matching the name first is what the bug looks like after a
 * half-fix: on the pilot account `completed` is a real, seeded, ACTIVE status
 * name with zero rows behind it, so an exact-first rule resolves "completed"
 * straight back to the empty bucket and changes nothing.
 *
 * Nobody asking "which jobs did Owen complete" means that bucket; they mean
 * the work is done. Precision is not lost, because the specific buckets have
 * their own distinct names — `to_invoice`, `invoice_sent`, `payment_issues` —
 * and none of those is a meaning word, so they still resolve narrowly.
 *
 * Returns the family for a meaning word, a single-element array for an exact
 * name, or `null` when the term means nothing here — callers should then say
 * so rather than silently running unfiltered, which is the trap
 * `checkQueryFilters` exists to close one field over.
 */
export function resolveStatusFilterNames(
  rows: { name: string; system_key: string | null }[],
  term: string,
): string[] | null {
  const wanted = normaliseStatusTerm(term);
  if (!wanted) return null;

  const key = STATUS_TERM_KEYS[wanted];
  if (!key) {
    const exact = rows.find(r => normaliseStatusTerm(r.name) === wanted);
    return exact ? [exact.name] : null;
  }

  const names = rows.filter(r => r.system_key === key).map(r => r.name);
  // No rows (statuses not seeded) → fall back to the default names carrying
  // that key, so the filter still narrows instead of vanishing.
  if (names.length) return names;
  const fallback = Object.entries(RAW_KEY).filter(([, k]) => k === key).map(([n]) => n);
  return fallback.length ? fallback : null;
}

/**
 * For DB `.in('status', ...)` filters: resolve the account's status NAMES that
 * map to the given system_keys, so a name-based DB filter still matches
 * renamed statuses. Falls back to the supplied defaults when the account has
 * no matching rows (e.g. job_statuses not yet seeded).
 */
/**
 * Every status NAME that `resolveStatusKey(name, buildStatusKeyMap(rows))`
 * would put in one of `keys` — the SQL twin of filtering a loaded list with
 * the account's key map.
 *
 * Unlike `statusNamesForKeys` this is not a fallback: it is the account's
 * matching rows PLUS the `RAW_KEY` defaults the account has no row for, which
 * is exactly how the client classifies a name it cannot find in the map. A
 * server filter and a browser filter that disagree about which jobs are "in
 * progress" would make the same list depend on how many jobs the account has.
 */
export function statusNamesMatchingKeys(
  rows: { name: string; system_key: string | null }[],
  keys: Exclude<SystemKey, null>[],
): string[] {
  const own = new Set(rows.map(r => r.name));
  const names = rows
    .filter(r => r.system_key && keys.includes(r.system_key as Exclude<SystemKey, null>))
    .map(r => r.name);
  for (const [name, key] of Object.entries(RAW_KEY)) {
    if (!own.has(name) && key && keys.includes(key)) names.push(name);
  }
  return names;
}

export function statusNamesForKeys(
  rows: { name: string; system_key: string | null }[],
  keys: Exclude<SystemKey, null>[],
  defaults: string[],
): string[] {
  const names = rows.filter(r => r.system_key && keys.includes(r.system_key as Exclude<SystemKey, null>)).map(r => r.name);
  return names.length ? names : defaults;
}
